import {
  resetMultiPolling,
  setIdsToPoll,
  startMultiStatusPoll,
  stopMultiStatusPoll,
} from 'common/publish-steps/containers/publish-steps-actions';
import isEqual from 'lodash.isequal';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { publishSteps } from './app-configs';


/**
 * This hook polls for the status of several items at once.
 * Polling interval is determined by MW
 * It can also refetch the data when needed
 *
 * @param {*} items The items that need polling
 * @param {*} refetchCb The callback to refetch the data
 * @returns
 */
const useMultiStatusPoll = (items = [], refetchCb, isConnected = false) => {
  const {
    multiStatusInfo,
    idsToPoll,
    shouldResetMultiPolling,
  } = useSelector(state => state.currentStatus);
  const [prevItems, setPrevItems] = useState([]);
  const [prevMultiStatusInfo, setPrevMultiStatusInfo] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => () => dispatch(stopMultiStatusPoll()), []);

  useEffect(() => {
    if (isConnected) {
      dispatch(stopMultiStatusPoll());
    }
  }, [isConnected]);

  useEffect(() => {
    if (shouldResetMultiPolling && !isConnected) {
      dispatch(stopMultiStatusPoll());
      if (items?.length) {
        dispatch(startMultiStatusPoll(items?.map(i => i.id)));
      }
      dispatch(resetMultiPolling({ shouldReset: false }));
    }
  }, [shouldResetMultiPolling]);

  useDeepCompareEffect(() => {
    if (!isEqual(
      items?.map(i => ({ id: i.id })),
      prevItems?.map(i => ({ id: i.id }))) &&
      !isConnected
    ) {
      setPrevItems(items);
      if (items?.length) {
        dispatch(startMultiStatusPoll(items?.map(i => i.id)));
      } else {
        dispatch(stopMultiStatusPoll());
      }
    }
  }, [items, isConnected]);

  useDeepCompareEffect(() => {
    if (!isEqual(multiStatusInfo, prevMultiStatusInfo)) {
      // handles 410
      if (
        multiStatusInfo?.length &&
        !isEqual(idsToPoll, multiStatusInfo?.map(i => i.newId || i.id)) &&
        items?.length === multiStatusInfo.length
      ) {
        if (refetchCb) {
          refetchCb();
        }
        dispatch(setIdsToPoll(multiStatusInfo?.map(i => i.newId || i.id)));
      }

      // If one status in multistatusinfo is done that was not done in previous multistatusInfo,
      // we should refetch
      if (
        refetchCb &&
        prevMultiStatusInfo?.length &&
        multiStatusInfo?.find(i =>
          i.operationInfo?.status === publishSteps.done.status &&
          prevMultiStatusInfo?.find(
            p => p.id === i.id,
          )?.operationInfo?.status < publishSteps.done.status,
        )
      ) {
        refetchCb(multiStatusInfo?.find(i =>
          i.operationInfo?.status === publishSteps.done.status &&
          prevMultiStatusInfo?.find(
            p => p.id === i.id,
          )?.operationInfo?.status < publishSteps.done.status,
        )?.id);
      }
      setPrevMultiStatusInfo(multiStatusInfo);
    }
  }, [multiStatusInfo]);

  return [multiStatusInfo];
};

export default useMultiStatusPoll;
